import { useEffect, useReducer } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useNavigationDestinations,
  filterDestinationsByIds,
  DESKTOP_SIDEBAR_IDS,
  resolveDesktopSidebarTestId,
  type NavigationDestination,
  type NavigationDestinationId,
} from '../features/navigation/customerNavigationModel.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../features/hub/customerHubPaths.js';
import { isGuestAuthPath } from '../features/auth/routing/isGuestAuthPath.js';
import { ORDERS_INTENT_PLATFORM_RECOVERY_PATH } from '../features/auth/routing/resolveGuestAuthRedirectTarget.js';
import {
  BOTTOM_NAV_AUTH_INTENT_STATE_KEY,
  clearBottomNavMemoryAuthIntent,
  clearOrdersAuthGateIntent,
  isBottomNavAuthChromePath,
  resolveBottomNavActiveTab,
  setBottomNavMemoryAuthIntent,
  type BottomNavTab,
} from './bottomNavActiveTab.js';
import {
  consumePostAuthOrdersIntent,
  peekInMemoryReturnTarget,
  peekPlatformReturnTarget,
  peekPostAuthOrdersIntent,
  peekReturnTarget,
  savePlatformReturnTarget,
  savePostAuthOrdersIntent,
} from '../shared/session/returnTarget.js';

const LINK_BASE =
  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]';

function buildLinkClassName(isActive: boolean): string {
  if (isActive) {
    return [
      LINK_BASE,
      'bg-[var(--color-accent)]',
      'text-[var(--color-accent-foreground)]',
      'font-semibold',
    ].join(' ');
  }
  return [
    LINK_BASE,
    'text-[var(--color-on-surface-muted)]',
    'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
  ].join(' ');
}

/** Map sidebar destination ids → BottomNavTab for shared active-tab SSOT. */
function destinationIdToTab(id: NavigationDestinationId): BottomNavTab | null {
  if (id === 'browse') {
    return 'browse';
  }
  if (id === 'home') {
    return 'shop';
  }
  if (id === 'scan') {
    return 'scan';
  }
  if (id === 'orders' || id === 'loyalty') {
    return 'orders';
  }
  if (id === 'shops') {
    return 'shops';
  }
  return null;
}

function isBrowsePathname(pathname: string): boolean {
  return pathname === '/browse' || pathname.startsWith('/browse/');
}

/** Oblíbené hub paths — favorites / account shop / retail shop; never Nakupovat (/browse). */
function isHomeHubPathname(pathname: string, hrefPath: string): boolean {
  if (isBrowsePathname(pathname)) {
    return false;
  }
  if (pathname === hrefPath || pathname.startsWith(`${hrefPath}/`)) {
    return true;
  }
  return (
    pathname === '/favorites' ||
    pathname.startsWith('/favorites/') ||
    /(?:^|\/)account\/shop(?:\/|$)/.test(pathname) ||
    ((pathname === '/shop' || pathname.startsWith('/shop/')) &&
      !pathname.startsWith('/shop/scanner'))
  );
}

function pathMatchesDestination(
  destination: NavigationDestination,
  pathname: string,
  search: string,
): boolean {
  const hrefPath = destination.href.split('?')[0] ?? destination.href;
  const hrefQuery = destination.href.includes('?')
    ? destination.href.slice(destination.href.indexOf('?'))
    : '';

  if (destination.id === 'browse') {
    return isBrowsePathname(pathname) || pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
  }
  if (destination.id === 'home') {
    return isHomeHubPathname(pathname, hrefPath);
  }
  if (destination.id === 'loyalty') {
    const onDedicatedLoyalty =
      pathname === '/account/loyalty' || pathname.startsWith('/account/loyalty/');
    const onOrdersLoyaltyTab =
      (pathname === '/account/orders' || pathname.startsWith('/account/orders/')) &&
      search.includes('tab=loyalty');
    return onDedicatedLoyalty || onOrdersLoyaltyTab;
  }
  if (destination.id === 'orders') {
    const onOrders =
      pathname === '/account/orders' || pathname.startsWith('/account/orders/');
    return onOrders && !search.includes('tab=loyalty');
  }
  if (hrefQuery.length > 0) {
    return pathname === hrefPath && search === hrefQuery;
  }
  return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
}

function resolveSidebarItemActive(input: {
  readonly destination: NavigationDestination;
  readonly activeTab: BottomNavTab;
  readonly pathname: string;
  readonly search: string;
  readonly onAuthChrome: boolean;
}): boolean {
  const { destination, activeTab, pathname, search, onAuthChrome } = input;

  // Auth chrome: primary Purchases (orders) stays lit via shared intent SSOT.
  if (onAuthChrome) {
    if (destination.id === 'orders') {
      return activeTab === 'orders';
    }
    if (destination.id === 'home') {
      return activeTab === 'shop';
    }
    return false;
  }

  // Orders vs loyalty share BottomNavTab 'orders'; exclusive path/query matching
  // so /account/orders?tab=loyalty (or /account/loyalty) does not dual-highlight.
  if (destination.id === 'loyalty' || destination.id === 'orders') {
    return pathMatchesDestination(destination, pathname, search);
  }

  const tab = destinationIdToTab(destination.id);
  if (tab === null) {
    return pathMatchesDestination(destination, pathname, search);
  }
  return activeTab === tab;
}

interface SidebarNavItemProps {
  readonly destination: NavigationDestination;
  readonly isActive: boolean;
  readonly href: string;
  readonly authGateState?: Record<string, string>;
  readonly onAuthGateClick?: () => void;
  readonly t: ReturnType<typeof useTranslation<'customer'>>['t'];
}

function SidebarNavItem({
  destination,
  isActive,
  href,
  authGateState,
  onAuthGateClick,
  t,
}: SidebarNavItemProps): JSX.Element {
  return (
    <li key={destination.id}>
      <Link
        to={href}
        state={authGateState}
        data-testid={resolveDesktopSidebarTestId(destination.testId)}
        aria-current={isActive ? 'page' : undefined}
        className={buildLinkClassName(isActive)}
        onClick={() => {
          onAuthGateClick?.();
        }}
      >
        <destination.Icon className="h-5 w-5 shrink-0" />
        <span className="truncate">{t(destination.labelKey)}</span>
      </Link>
    </li>
  );
}

/** Desktop (`lg+`) primary nav — Nakupovat · Oblíbené · Scan · Purchases · Shops (+ Loyalty when signed in). */
export function CustomerDesktopSidebar(): JSX.Element | null {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const location = useLocation();
  const navigate = useNavigate();
  const [, bumpAuthIntentEpoch] = useReducer((n: number) => n + 1, 0);
  const { session } = useCustomerSession();
  const { destinations: allDestinations, hideNavigation } = useNavigationDestinations();

  useEffect(() => {
    if (!isBottomNavAuthChromePath(location.pathname, tenantCode)) {
      clearOrdersAuthGateIntent();
    }
  }, [location.pathname, tenantCode]);

  // After post-auth orders intent, resume platform orders once signed in.
  // Guest must keep the intent while soft-Navigate parks on /sign-in — consuming
  // here bounced guests back to /account/orders (blank shell vs RequireCustomerAuth).
  useEffect(() => {
    if (!peekPostAuthOrdersIntent()) {
      return;
    }
    if (session === null) {
      return;
    }
    consumePostAuthOrdersIntent();
    clearBottomNavMemoryAuthIntent();
    navigate(ORDERS_INTENT_PLATFORM_RECOVERY_PATH, { replace: true });
  }, [navigate, session]);

  if (hideNavigation) {
    return null;
  }

  const onGuestAuthForm =
    isGuestAuthPath(location.pathname) ||
    isBottomNavAuthChromePath(location.pathname, tenantCode);
  const globallySignedIn = isGloballySignedInChrome({ session });
  const signedInChrome = globallySignedIn && !onGuestAuthForm;
  const tokenSignedIn = signedInChrome;

  const authIntentRaw = (location.state as Record<string, unknown> | null)?.[
    BOTTOM_NAV_AUTH_INTENT_STATE_KEY
  ];
  const authIntentTab =
    authIntentRaw === 'orders' || authIntentRaw === 'shop' || authIntentRaw === 'more'
      ? authIntentRaw
      : null;

  const returnTargetPath =
    peekInMemoryReturnTarget() ??
    peekReturnTarget({ tenantCode }) ??
    peekPlatformReturnTarget();

  void bumpAuthIntentEpoch;

  const activeTab = resolveBottomNavActiveTab(location.pathname, tenantCode, {
    authIntentTab,
    returnTargetPath,
    hasOrdersAuthIntent: peekPostAuthOrdersIntent(),
  });

  const onAuthChrome = isBottomNavAuthChromePath(location.pathname, tenantCode);
  const primaryItems = filterDestinationsByIds(allDestinations, DESKTOP_SIDEBAR_IDS);

  const renderItems = (items: readonly NavigationDestination[]): JSX.Element[] =>
    items.map((destination) => {
      const needsAuthGate = !tokenSignedIn && destination.id === 'orders';
      const href = needsAuthGate ? '/sign-in' : destination.href;
      const authGateState = needsAuthGate
        ? { [BOTTOM_NAV_AUTH_INTENT_STATE_KEY]: 'orders' }
        : undefined;
      const isActive = resolveSidebarItemActive({
        destination,
        activeTab,
        pathname: location.pathname,
        search: location.search,
        onAuthChrome,
      });

      return (
        <SidebarNavItem
          key={destination.id}
          destination={destination}
          isActive={isActive}
          href={href}
          authGateState={authGateState}
          t={t}
          onAuthGateClick={
            needsAuthGate
              ? () => {
                  // Peer RequireCustomerAuth orders park: durable platform path + intent.
                  savePlatformReturnTarget(ORDERS_INTENT_PLATFORM_RECOVERY_PATH);
                  savePostAuthOrdersIntent();
                  setBottomNavMemoryAuthIntent('orders');
                  bumpAuthIntentEpoch();
                  if (location.pathname === href && authGateState !== undefined) {
                    navigate(href, { replace: true, state: authGateState });
                  }
                }
              : () => {
                  clearOrdersAuthGateIntent();
                }
          }
        />
      );
    });

  return (
    <nav
      aria-label={t('nav.ariaLabel')}
      className="hidden w-56 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-surface)] lg:sticky lg:top-[calc(env(safe-area-inset-top,0px)+3.5rem)] lg:block lg:max-h-[calc(100dvh-env(safe-area-inset-top,0px)-3.5rem)] lg:overflow-y-auto"
      data-testid="customer-desktop-sidebar"
    >
      <ul className="flex flex-col gap-1 p-3">{renderItems(primaryItems)}</ul>
    </nav>
  );
}
